import { APP_ROUTES, formatQueryParams } from '@zorro/common-utils';
import {
  Stepper,
  type StepperStepProps,
} from '@zorro/design-system/v2/Stepper';
import { Stack } from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';

import { useAnalytics } from '../../Analytics';
import {
  ManualSubmissionStepChangeDirection,
  ManualSubmissionStepType,
  getManualSubmissionHidden,
  getManualSubmissionStepConfig,
} from './ManualSubmissionUtils';

type StepperLinkProps = StepperStepProps & {
  manualSubmissionStep: ManualSubmissionStepType;
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  isDon: boolean;
  onNavigate: () => void;
};

const StepperLink = ({
  manualSubmissionStep,
  onboardingPeriodId,
  employerId,
  employeeId,
  isDon,
  onNavigate,
  ...props
}: StepperLinkProps) => {
  const { label } = getManualSubmissionStepConfig(manualSubmissionStep, isDon);

  if (props.state === 'stepInactive') {
    return <Stepper.Step {...props} label={label} />;
  }

  const route: keyof typeof APP_ROUTES = isDon
    ? 'DON_EMPLOYEE_PLANS_TAB'
    : 'OPS_EMPLOYEE_PLANS_TAB';
  const url = APP_ROUTES[route]({
    employerId,
    employeeId,
    onboardingPeriodId,
  });

  return (
    <Stepper.Link
      {...props}
      href={formatQueryParams(url, { step: manualSubmissionStep })}
      label={label}
      onClick={onNavigate}
    />
  );
};

type Props = {
  step: ManualSubmissionStepType;
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  isDon: boolean;
};

export const ManualSubmissionStepper = ({
  step,
  employerId,
  employeeId,
  onboardingPeriodId,
  isDon,
}: Props) => {
  const { isManagement } = useRoles();
  const { sendEvent } = useAnalytics();

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
    enabled: isManagement,
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
    enabled: isManagement,
  });

  if (!employee || !onboardingPeriod) {
    return null;
  }

  const { stepIndex } = getManualSubmissionStepConfig(
    step,
    isDon,
    onboardingPeriod
  );

  const handleNavigate = (
    targetStep: ManualSubmissionStepType,
    targetStepIndex: number
  ) => {
    if (targetStep === step) {
      return;
    }

    void sendEvent('manual_election_step_change', {
      onboarding_period_id: onboardingPeriodId,
      from_step: step,
      to_step: targetStep,
      direction:
        targetStepIndex < stepIndex
          ? ManualSubmissionStepChangeDirection.BACK
          : ManualSubmissionStepChangeDirection.FORWARD,
      trigger: 'stepper',
    });
  };

  return (
    <Stack>
      <Stepper
        allowNextStepsSelect={false}
        active={stepIndex}
        wrap={false}
        size="sm"
      >
        {Object.values(ManualSubmissionStepType).map((manualSubmissionStep) => {
          const isHidden = getManualSubmissionHidden(
            manualSubmissionStep,
            onboardingPeriod
          );

          if (isHidden) {
            return null;
          }

          const { stepIndex: targetStepIndex } = getManualSubmissionStepConfig(
            manualSubmissionStep,
            isDon,
            onboardingPeriod
          );

          return (
            <StepperLink
              key={manualSubmissionStep}
              manualSubmissionStep={manualSubmissionStep}
              onboardingPeriodId={onboardingPeriodId}
              onNavigate={() =>
                handleNavigate(manualSubmissionStep, targetStepIndex)
              }
              isDon={isDon}
              employerId={employerId}
              employeeId={employeeId}
            />
          );
        })}
      </Stepper>
    </Stack>
  );
};
